import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { useLocation, useNavigate, useResolvedPath, useSearchParams } from "react-router";
import { api, type BoardView, type Task } from "@/api";
import { type BoardFilter, parseFilter, queryOf, writeFilter } from "@/lib/filter";
import { useData } from "@/store";
import { useTheme } from "@/store/theme";

export type { BoardFilter };

const subscribeMedia = (q: string) => (cb: () => void) => {
	const m = matchMedia(q);
	m.addEventListener("change", cb);
	return () => m.removeEventListener("change", cb);
};

/** A live media query (false without a DOM). */
export function useMedia(query: string): boolean {
	return useSyncExternalStore(
		subscribeMedia(query),
		() => matchMedia(query).matches,
		() => false,
	);
}

/** Dark: the theme picked in Settings, else the system's. */
export const useDark = () => {
	const system = useMedia("(prefers-color-scheme: dark)");
	const theme = useTheme((s) => s.theme);
	return theme === "auto" ? system : theme === "dark";
};
/** Phone layout (POC: max-width 640px): neighbours peek, detail as a bottom sheet. */
export const MOBILE = "(max-width: 640px)";
export const useMobile = () => useMedia(MOBILE);

/** Loads the snapshot once. Afterwards the kit pushes the board (its replica, kept current by the sockets of the
 *  home and every Connected instance): no polling. */
export function useRefresh() {
	const load = useData((s) => s.load);
	useEffect(() => {
		void load();
	}, [load]);
}

const withParam = (search: string, k: string, v: string | null) => {
	const q = new URLSearchParams(search);
	if (v) q.set(k, v);
	else q.delete(k);
	const s = q.toString();
	return s ? `?${s}` : "";
};

/**
 * A page that lives in a search param (?id=, ?usage=1, ?secret=\u2026). Opening and closing replace the history entry
 * (no back step per panel). close() reads the live URL, not the render's location: a sheet can close before React
 * re-renders.
 */
export function useParam(name: string) {
	const location = useLocation();
	const navigate = useNavigate();
	const value = new URLSearchParams(location.search).get(name);
	/** `swap`: replace the open value instead of stacking another history entry. */
	// No history entries for panels (Michael, 2026-10-24): an iOS Safari edge swipe must not close them, so back is
	// not a way to close a panel either; Close, Escape and the sheet's gesture are. `swap` is kept for callers.
	const open = (next: string, _swap = false) => {
		if (next === value) return;
		navigate({ search: withParam(location.search, name, next) }, { replace: true, state: location.state });
	};
	const close = () => navigate({ search: withParam(window.location.search, name, null) }, { replace: true, state: window.history.state?.usr });
	/** Is it open right now (live URL)? */
	const isOpen = () => new URLSearchParams(window.location.search).has(name);
	return { value, open, close, isOpen };
}

/** Detail selection in `?id=`, shared by board and graph. */
export function useSelection() {
	const { value: id, open, close, isOpen } = useParam("id");
	return { id, open, close, isOpen };
}

/** An on/off modal in `?<name>=1` (the board's filter sheet). */
export function useFlag(name: string) {
	const p = useParam(name);
	const on = p.value === "1";
	return { on, open: () => p.open("1"), close: p.close, toggle: () => (on ? p.close() : p.open("1")) };
}

/**
 * The pages after this one (router.tsx: a page's child routes). `open` = the child segment the URL goes on with (one
 * at a time: the next page), `toggle(seg)` opens it as the next page or closes it back to this one, `close()` closes
 * this page (back to its parent). Like the ?param modals they replace the history entry (Michael, 2026-10-24: an iOS
 * edge swipe must not close pages); the query (the scope's ?project=, ?id=) rides along.
 */
export function usePage() {
	const navigate = useNavigate();
	const { pathname, search } = useLocation();
	const here = useResolvedPath(".").pathname.replace(/\/$/, "");
	const parent = useResolvedPath("..").pathname;
	const rest = pathname.startsWith(`${here}/`) ? pathname.slice(here.length + 1) : "";
	const open = rest ? decodeURIComponent(rest.split("/")[0]!) : null;
	const go = (to: string) => navigate({ pathname: to, search }, { replace: true });
	const to = (segs: string[]) => `${here}/${segs.map(encodeURIComponent).join("/")}`;
	return {
		open,
		toggle: (seg: string) => go(open === seg ? here || "/" : to([seg])),
		/** a page further down at once (a secret's Change: <id>/edit) */
		show: (...segs: string[]) => go(to(segs)),
		close: () => go(parent),
	};
}

/** Board filter in the URL (lib/filter.ts). Setting it replaces the history entry (no back-step per tap). */
export function useBoardFilter(): [BoardFilter, (next: BoardFilter | ((f: BoardFilter) => BoardFilter)) => void] {
	const [params, setParams] = useSearchParams();
	const { state } = useLocation();
	const key = params.toString();
	// biome-ignore lint/correctness/useExhaustiveDependencies: parsed once per URL
	const filter = useMemo(() => parseFilter(params), [key]);
	const set = (next: BoardFilter | ((f: BoardFilter) => BoardFilter)) =>
		setParams((p) => writeFilter(p, typeof next === "function" ? next(parseFilter(p)) : next), { replace: true, state });
	return [filter, set];
}

export type Column = { status: string; open: boolean; color: string | null; tasks: Task[] };

/** The board for a filter, from the core (board.view: columns in manual order, people with open counts), kept
 *  current; the columns' tasks are the snapshot's objects (memoised cards keep their props). */
export function useBoardView(filter: BoardFilter, hits: ReadonlySet<string> | null): { columns: Column[]; people: BoardView["people"] } | null {
	const [view, setView] = useState<BoardView | null>(null);
	const snapshot = useData((s) => s.snapshot);
	const q = queryOf(filter, hits);
	const key = JSON.stringify(q);
	const w = useRef<ReturnType<typeof api.board.view> | null>(null);
	useEffect(() => {
		w.current = api.board.view(q, setView);
		return () => w.current?.close();
		// biome-ignore lint/correctness/useExhaustiveDependencies: opened once; the filter goes in by update
	}, []);
	// biome-ignore lint/correctness/useExhaustiveDependencies: keyed on the query
	useEffect(() => w.current?.update(q), [key]);
	return useMemo(() => {
		if (!view || !snapshot) return null;
		const byId = new Map(snapshot.tasks.map((t) => [t.id, t]));
		const columns = view.columns.map((c) => ({ status: c.status, open: c.open, color: c.color, tasks: c.tasks.flatMap((id) => byId.get(id) ?? []) }));
		return { columns, people: view.people };
	}, [view, snapshot]);
}

/** Full-text hits for `q` (title + body), 250 ms after typing stops; null while there are none yet. */
export function useSearchHits(q: string): ReadonlySet<string> | null {
	const [hits, setHits] = useState<{ q: string; ids: ReadonlySet<string> } | null>(null);
	const text = q.trim();
	useEffect(() => {
		if (!text) return;
		let live = true;
		const t = setTimeout(() => {
			api.tasks.search(text).then(
				(ids) => live && setHits({ q: text, ids: new Set(ids) }),
				() => {},
			);
		}, 250);
		return () => {
			live = false;
			clearTimeout(t);
		};
	}, [text]);
	return text && hits?.q === text ? hits.ids : null;
}
